import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import i18n from "../../../../i18n";
import styles from "../EditAppointmentModal/EditAppointmentModal.module.css";
import seriesStyles from "./CreateAppointmentModal.module.css";
import {
  useValidateAppointmentChangeMutation,
  useCreateAppointmentFastMutation,
  useGetAllMetaTablesQuery,
  useGetProtocolsByCompanyQuery,
} from "../../../../services/api/utils.api";
import { useLazyGetUsersByCompanyIdWithRecommendsQuery, useUpdateTherapistsUsersConnectionsMutation, useLazyGetCompanyDevicesQuery } from "../../../../services/api/user.api";
import { useAppointmentsContext } from "../../context/AppointmentsContext";
import { useAppSelector } from "../../../../services/hooks";
import { adminSelector } from "../../../../services/selectors/user.selectors";
import { appointmentsProgramsDataSelector } from "../../../../services/selectors/utils.selectors";
import { timeToMinutes } from "../../../../utils/utils";
import {
  IAppointmentProgramProtocol,
  ISelectedProtocol,
} from "../../../../services/types/utils.types";
import TreatmentPlanModal from "../TreatmentPlanModal/TreatmentPlanModal";
import DeviceConfigurationModal from "../DeviceConfigurationModal/DeviceConfigurationModal";
import { AppointmentDevice, StaffDevice } from "../../../../services/types/device.types";
import {
  getDeviceConfigSummary,
  getDeviceSummaryLabel,
} from "../../../../utils/deviceConfiguration";
import { useLazyGetUserTreatmentProtocolQuery } from "../../../../services/api/treatmentProtocols.api";
import {
  PROTOCOL_FREQUENCY_AFTER_EACH,
  TProtocolFrequencyType,
  TreatmentProtocol,
  protocolFrequencyTakesEveryN,
} from "../../../../services/types/treatmentProtocol.types";

/** Devices are operated by technicians (roleId 8); the machines themselves belong to the company. */
const TECHNICIAN_ROLE_ID = 8;

/** The modal's own defaults, used when resetting a protocol pre-fill. */
const DEFAULT_APPOINTMENT_MINUTES = 50;
const DEFAULT_MEDIA_ID = 1;

/** startTime + minutes, as "HH:MM". */
function addMinutesToTime(startTime: string, minutes: number): string {
  const [hours, mins] = startTime.split(":").map(Number);
  const total = hours * 60 + mins + minutes;
  const wrapped = ((total % 1440) + 1440) % 1440;
  return `${String(Math.floor(wrapped / 60)).padStart(2, "0")}:${String(wrapped % 60).padStart(2, "0")}`;
}

/** API user row: skip StatusId === 0 in appointment dropdowns */
const notInactiveUser = (u: any) => (u.StatusId ?? u.statusId ?? 1) !== 0;

type UserOption = { id: number; guid: string; fullName: string; phone?: string };

interface ICreateAppointmentModalProps {
  onClose: () => void;
  onSuccess: () => void;
  initialDate: string;
  initialTime?: string;
  therapistId?: number;
}

interface IConflict {
  type: string;
  message: string;
  severity: "warning" | "info";
}

interface ITherapistAvailability {
  dayId: number;
  startTime: string;
  endTime: string;
}

const CreateAppointmentModal: React.FC<ICreateAppointmentModalProps> = ({
  onClose,
  onSuccess,
  initialDate,
  initialTime = "09:00",
  therapistId: initialTherapistId,
}) => {
  const isRtl = i18n.language === "he";
  const admin = useAppSelector(adminSelector);
  const { therapistsList } = useAppointmentsContext();
  const appointmentsProgramsData = useAppSelector(appointmentsProgramsDataSelector);

  // API hooks
  const [validateChange] = useValidateAppointmentChangeMutation();
  const [postAppointment, { isLoading: isCreating }] = useCreateAppointmentFastMutation();
  const [searchPatients, { isFetching: isSearchingPatients }] = useLazyGetUsersByCompanyIdWithRecommendsQuery();
  const [updateTherapistsUsersConnections, { isLoading: isLinking }] = useUpdateTherapistsUsersConnectionsMutation();
  const [getCompanyDevices, { isFetching: isFetchingDevices }] = useLazyGetCompanyDevicesQuery();

  useGetAllMetaTablesQuery(admin.companyId, { skip: !admin.companyId });
  const { data: protocolsData = [] } = useGetProtocolsByCompanyQuery(
    { companyId: admin.companyId },
    { skip: !admin.companyId }
  );

  // Form state
  const [date, setDate] = useState(initialDate);
  const [startTime, setStartTime] = useState(initialTime);
  const [endTime, setEndTime] = useState(() => {
    const [h, m] = initialTime.split(":").map(Number);
    const dateObj = new Date();
    dateObj.setHours(h, m + 50);
    return dateObj.toTimeString().slice(0, 5);
  });
  const [therapistId, setTherapistId] = useState(initialTherapistId || 0);
  const [therapistGuid, setTherapistGuid] = useState("");
  const [patientId, setPatientId] = useState(0);
  const [patientGuid, setPatientGuid] = useState("");
  const [selectedPatientName, setSelectedPatientName] = useState("");
  const [mediaId, setMediaId] = useState(1);
  const [comments, setComments] = useState("");
  const [repeatCount, setRepeatCount] = useState(1);
  const [appointmentType, setAppointmentType] = useState<'treatment' | 'other'>('treatment');

  // Series settings state
  const [isPlanPanelOpen, setIsPlanPanelOpen] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const [appointmentsDurationsId, setAppointmentsDurationsId] = useState(0);
  const [appointmentsSequenceId, setAppointmentsSequenceId] = useState(0);
  const [selectedProtocols, setSelectedProtocols] = useState<ISelectedProtocol[]>([]);
  const [protocolsValid, setProtocolsValid] = useState(true);
  const [durationManuallySet, setDurationManuallySet] = useState(false);
  // Plan configured flag + section-2 details collected from TreatmentPlanModal
  const [planConfigured, setPlanConfigured] = useState(false);
  const [planDetails, setPlanDetails] = useState<{
    treatmentPlanName?: string;
    roleId?: number;
    genderId?: number;
    migzarId?: number;
    treatmentRoomId?: number;
    fundingSourcesId?: number;
    treatmentTypesId?: number;
  }>({});

  // UI state
  const [patientSearch, setPatientSearch] = useState("");
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [allPatients, setAllPatients] = useState<any[]>([]);
  const [showTherapistDropdown, setShowTherapistDropdown] = useState(false);
  const [therapistSearch, setTherapistSearch] = useState("");
  const [showMediaDropdown, setShowMediaDropdown] = useState(false);
  
  // Validation state
  const [conflicts, setConflicts] = useState<IConflict[]>([]);
  const [therapistAvailability, setTherapistAvailability] = useState<ITherapistAvailability[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [associationError, setAssociationError] = useState("");
  const [deviceError, setDeviceError] = useState("");
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [staffDevices, setStaffDevices] = useState<StaffDevice[]>([]);
  const [appointmentDevice, setAppointmentDevice] = useState<AppointmentDevice | null>(null);

  // Linked options (filtered by TherapistsUsers association)
  const [patientsForTherapist, setPatientsForTherapist] = useState<UserOption[]>([]);
  const [therapistsForPatient, setTherapistsForPatient] = useState<UserOption[]>([]);
  const [isFetchingLinked, setIsFetchingLinked] = useState(false);

  // Refs
  const patientDropdownRef = useRef<HTMLDivElement>(null);
  const therapistDropdownRef = useRef<HTMLDivElement>(null);
  const mediaDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setAssociationError("");
    setDeviceError("");
  }, [patientId, therapistId]);

  // ---- The patient's assigned treatment protocol ----
  // A regular protocol only pre-fills and every field stays editable. A device protocol binds:
  // it dictates the modality, the montage and the session length, and a patient without one
  // cannot be booked on a device at all — the server enforces the same rule on save.
  const [fetchUserProtocol] = useLazyGetUserTreatmentProtocolQuery();
  const [assignedProtocol, setAssignedProtocol] = useState<TreatmentProtocol | null>(null);
  // Until the lookup answers, "no protocol" is unknown rather than false — otherwise the block
  // below would flash on every patient pick.
  const [protocolChecked, setProtocolChecked] = useState(false);

  /**
   * Clears everything a protocol may have filled in. Runs on every patient change so the previous
   * patient's protocol never leaks into the next one — including the case where the next patient
   * has no protocol at all and nothing would otherwise overwrite the stale values.
   */
  const resetProtocolPrefill = useCallback(() => {
    setAppointmentsSequenceId(0);
    setRepeatCount(1);
    setSelectedProtocols([]);
    setPlanDetails({});
    setPlanConfigured(false);
    setMediaId(DEFAULT_MEDIA_ID);
    setAppointmentDevice(null);
    setDeviceError("");

    // Back to the modal's own default length, unless the user set the times themselves.
    if (!durationManuallySet) {
      setEndTime(addMinutesToTime(startTime, DEFAULT_APPOINTMENT_MINUTES));
    }
  }, [durationManuallySet, startTime]);

  /**
   * A protocol's duration drives the appointment length, unless the user set the times themselves.
   * Both protocol kinds carry one — the device kind keeps it inside its device config.
   */
  const applyProtocolDuration = useCallback(
    (minutes?: number | null) => {
      if (!minutes || durationManuallySet) return;
      setEndTime(addMinutesToTime(startTime, minutes));
    },
    [durationManuallySet, startTime],
  );

  const applyRegularProtocol = useCallback(
    (protocol: TreatmentProtocol) => {
      if (protocol.appointmentsSequenceId) {
        setAppointmentsSequenceId(protocol.appointmentsSequenceId);
      }
      if (protocol.countOfAppointments && protocol.countOfAppointments > 0) {
        setRepeatCount(protocol.countOfAppointments);
      }
      if (protocol.questionnaires.length > 0) {
        setSelectedProtocols(
          protocol.questionnaires.map<ISelectedProtocol>((questionnaire) => ({
            protocolId: questionnaire.questionnaireCompaniesHeadersId,
            name:
              protocolsData.find((p) => p.id === questionnaire.questionnaireCompaniesHeadersId)
                ?.desc ?? "",
            frequencyType:
              (questionnaire.frequencyType as TProtocolFrequencyType) ??
              PROTOCOL_FREQUENCY_AFTER_EACH,
            everyNSessions: questionnaire.everyNSessions ?? 1,
          })),
        );
      }

      setPlanDetails((prev) => ({
        ...prev,
        // The protocol's own name is the plan name, so a created plan is traceable back to it.
        treatmentPlanName: protocol.name || prev.treatmentPlanName,
        roleId: protocol.roleId ?? prev.roleId,
        genderId: protocol.genderId ?? prev.genderId,
        migzarId: protocol.migzarId ?? prev.migzarId,
        treatmentRoomId: protocol.treatmentRoomId ?? prev.treatmentRoomId,
        fundingSourcesId: protocol.fundingSourcesId ?? prev.fundingSourcesId,
        treatmentTypesId: protocol.treatmentTypesId ?? prev.treatmentTypesId,
      }));
      setPlanConfigured(true);

      // Meeting mode lives outside the plan modal, in the appointment itself.
      if (protocol.mediasId) setMediaId(protocol.mediasId);

      applyProtocolDuration(protocol.appointmentsDurationsOpenMinutes);
    },
    [protocolsData, applyProtocolDuration],
  );

  useEffect(() => {
    // Reset first and synchronously: the stale prefill has to disappear the moment the patient
    // changes, not after the lookup for the new patient comes back.
    setAssignedProtocol(null);
    setProtocolChecked(false);
    resetProtocolPrefill();

    if (appointmentType !== "treatment" || !patientId) return;

    let cancelled = false;
    fetchUserProtocol(patientId)
      .unwrap()
      .then((protocol) => {
        if (cancelled) return;
        if (protocol) {
          setAssignedProtocol(protocol);
          if (protocol.kind === "regular") {
            applyRegularProtocol(protocol);
          } else {
            // A device protocol fills the device modal (see protocolTemplate below) and fixes
            // the appointment length; the lock effect keeps the window on it from here on.
            applyProtocolDuration(protocol.deviceConfig?.durationMinutes);
          }
        }
        setProtocolChecked(true);
      })
      .catch(() => {
        // A failed lookup must not silently turn into "this patient has no protocol": leave the
        // flag down so the device block never fires on a network error.
      });

    return () => {
      cancelled = true;
    };
    // applyRegularProtocol and resetProtocolPrefill are intentionally excluded: both change with
    // startTime, and re-running this on every time edit would keep overwriting the user's own
    // changes. The effect is meant to fire on a patient change only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentType, patientId, fetchUserProtocol]);

  const deviceProtocolTemplate =
    assignedProtocol?.kind === "device" ? assignedProtocol.deviceConfig ?? null : null;

  /**
   * The rescan gate. Once the patient has used up the treatments their protocol period allows,
   * nothing more can be booked on a device until they are scanned and given their next protocol —
   * a scan is a new course, not an exception inside this one. The server refuses either way; this
   * says so before the user fills the form.
   */
  const rescanDue = assignedProtocol?.kind === "device" && Boolean(assignedProtocol.rescanDue);
  const sessionsRemaining =
    assignedProtocol?.kind === "device" ? assignedProtocol.sessionsRemaining ?? null : null;

  // A series is booked in one go, so the whole series has to fit inside what is left of the
  // period — otherwise its tail would sit past a scan that has not happened yet.
  const exceedsRemainingSessions =
    sessionsRemaining !== null && sessionsRemaining > 0 && repeatCount > sessionsRemaining;

  /** A device protocol fixes the session length, so the end time follows it and stops being editable. */
  const lockedSessionMinutes = deviceProtocolTemplate?.durationMinutes ?? null;

  useEffect(() => {
    if (!lockedSessionMinutes) return;
    setEndTime(addMinutesToTime(startTime, lockedSessionMinutes));
  }, [lockedSessionMinutes, startTime]);

  const selectedTherapist = useMemo(
    () => therapistsList.find((t) => t.id === therapistId),
    [therapistsList, therapistId],
  );

  /**
   * What makes this a device session is the patient's protocol — not who is running it. Devices
   * belong to the company and every technician operates all of them, so the therapist only has to
   * be a technician.
   */
  const requiresDevice = appointmentType === "treatment" && Boolean(deviceProtocolTemplate);

  // The protocol arrives after the patient is chosen, so a therapist may already have been picked
  // — from the calendar column the user clicked — by the time the session turns out to be a device
  // one. Dropped here, or it would linger behind a hidden field and drive conflict warnings.
  useEffect(() => {
    if (requiresDevice && therapistId) {
      setTherapistId(0);
      setTherapistGuid("");
    }
  }, [requiresDevice, therapistId]);

  const openDeviceConfiguration = useCallback(async () => {
    try {
      // The window goes with the request so the picker can drop devices that are already busy
      // then, instead of surfacing the clash only after the user hits save.
      const devices = await getCompanyDevices({ date, startTime, endTime }).unwrap();

      setStaffDevices(devices);
      if (devices.length === 0) {
        setDeviceError(t("DEVICE_LIST_EMPTY"));
        return;
      }
      setDeviceError("");
      setShowDeviceModal(true);
    } catch {
      setDeviceError(t("SOMETHING_WENT_WRONG_TRY_AGAIN_LATER"));
    }
  }, [getCompanyDevices, date, startTime, endTime]);

  const handleTherapistSelect = useCallback(
    async (id: number, guid: string) => {
      const resolvedGuid = guid || therapistsList.find((t) => t.id === id)?.guid || "";

      setTherapistId(id);
      setTherapistGuid(resolvedGuid);
      setAppointmentDevice(null);
      setDeviceError("");
      setShowTherapistDropdown(false);

      // The machine no longer depends on who was picked, so choosing a technician opens nothing.
      // The device is configured from its own row, whenever the user is ready.
    },
    [therapistsList],
  );

  // Initial therapist GUID lookup
  useEffect(() => {
    if (initialTherapistId && therapistsList.length > 0) {
      const therapist = therapistsList.find(t => t.id === initialTherapistId);
      if (therapist) {
        setTherapistGuid(therapist.guid || "");
      }
    }
  }, [initialTherapistId, therapistsList]);

  // Initial Patient Load
  useEffect(() => {
    if (admin.companyId) {
      const fetchAllPatients = async () => {
        try {
          const result = await searchPatients({
            companyIds: [admin.companyId],
            isMinimal: true,
            roleIds: [1],
          }).unwrap();

          const reports = (result as any).Users || [];
          const users = reports.map((r: any) => r.Users).filter((u: any) => u && notInactiveUser(u));
          
          const mappedUsers = users.map((u: any) => ({
              id: u.Id,
              guid: u.Guid,
              fullName: u.FullName || `${u.FirstName} ${u.LastName}`,
              phone: u.Phone
          }));

          setAllPatients(mappedUsers);
        } catch (err) {
          console.error(err);
        }
      };
      fetchAllPatients();
    }
  }, [admin.companyId, searchPatients]);

  // Fetch patients linked to selected therapist (for treatment type only)
  useEffect(() => {
    if (appointmentType !== "treatment" || !therapistId || !therapistGuid || !admin.companyId) {
      setPatientsForTherapist([]);
      return;
    }
    const therapist = therapistsList.find((t) => t.id === therapistId);
    if (!therapist?.guid) return;

    const fetchLinked = async () => {
      setIsFetchingLinked(true);
      try {
        const result = await searchPatients({
          companyIds: [admin.companyId],
          isMinimal: true,
          roleIds: [1],
          therapistGuids: [therapist.guid || ""],
        }).unwrap();
        const reports = ((result as any).Users || []) as { Users?: any }[];
        const users = reports.map((r) => r.Users).filter((u: any) => u && notInactiveUser(u));
        const mapped: UserOption[] = users.map((u: any) => ({
          id: u.Id,
          guid: u.Guid ?? "",
          fullName: u.FullName || `${u.FirstName || ""} ${u.LastName || ""}`.trim(),
          phone: u.Phone,
        }));
        setPatientsForTherapist(mapped);
      } catch {
        setPatientsForTherapist([]);
      } finally {
        setIsFetchingLinked(false);
      }
    };
    fetchLinked();
  }, [appointmentType, therapistId, therapistGuid, admin.companyId, searchPatients, therapistsList]);

  // Fetch therapists linked to selected patient (for treatment type only)
  useEffect(() => {
    if (appointmentType !== "treatment" || !patientId || !patientGuid || !admin.companyId) {
      setTherapistsForPatient([]);
      return;
    }

    const fetchLinked = async () => {
      setIsFetchingLinked(true);
      try {
        const result = await searchPatients({
          companyIds: [admin.companyId],
          isMinimal: true,
          roleIds: [-1],
          userGuids: [patientGuid],
        }).unwrap();
        const reports = ((result as any).Users || []) as { Users?: any }[];
        const users = reports.map((r) => r.Users).filter((u: any) => u && notInactiveUser(u));
        const mapped: UserOption[] = users.map((u: any) => ({
          id: u.Id,
          guid: u.Guid ?? "",
          fullName: u.FullName || `${u.FirstName || ""} ${u.LastName || ""}`.trim(),
        }));
        setTherapistsForPatient(mapped);
      } catch {
        setTherapistsForPatient([]);
      } finally {
        setIsFetchingLinked(false);
      }
    };
    fetchLinked();
  }, [appointmentType, patientId, patientGuid, admin.companyId, searchPatients]);

  // Derived state (Memos) - always show all, sort linked first with marker
  const therapistOptions = useMemo(() => {
    const linkedIds = new Set(therapistsForPatient.map((t) => t.id));
    const allTherapists = therapistsList
      .filter((t) => t.isActive || t.id === therapistId)
      // A device session has to be run by a technician — any technician, since the machines are
      // the company's. Offering the rest would only produce a refusal on save.
      .filter((t) => !requiresDevice || t.roleId === TECHNICIAN_ROLE_ID || t.id === therapistId)
      .map((t) => ({
        id: t.id,
        guid: t.guid || "",
        fullName: t.name,
        isLinked: patientId ? linkedIds.has(t.id) : false,
        hasDevices: t.hasDevices ?? false,
      }));
    const search = therapistSearch.toLowerCase();
    const filtered = search
      ? allTherapists.filter((t) => t.fullName.toLowerCase().includes(search))
      : allTherapists;
    return [...filtered].sort((a, b) => (b.isLinked ? 1 : 0) - (a.isLinked ? 1 : 0));
  }, [therapistsList, therapistsForPatient, patientId, therapistId, therapistSearch, requiresDevice]);

  const patientOptions = useMemo(() => {
    const linkedIds = new Set(patientsForTherapist.map((p) => p.id));
    const allWithLinked = allPatients.map((p) => ({
      ...p,
      isLinked: therapistId ? linkedIds.has(p.id) : false,
    }));
    const query = patientSearch.toLowerCase();
    const filtered = query
      ? allWithLinked.filter((p) => p.fullName.toLowerCase().includes(query) || p.phone?.includes(query))
      : allWithLinked;
    return [...filtered].sort((a, b) => (b.isLinked ? 1 : 0) - (a.isLinked ? 1 : 0));
  }, [allPatients, patientsForTherapist, therapistId, patientSearch]);

  const isPatientTherapistLinked = useMemo(() => {
    if (!patientId || !therapistId) return true;
    return (
      patientsForTherapist.some((p) => p.id === patientId) ||
      therapistsForPatient.some((t) => t.id === therapistId)
    );
  }, [patientId, therapistId, patientsForTherapist, therapistsForPatient]);

  const handleLinkTherapistPatient = async () => {
    if (!therapistGuid || !patientGuid) return;
    try {
      await updateTherapistsUsersConnections({
        type: 1,
        body: { therapistsGuids: [therapistGuid], usersGuids: [patientGuid] },
      }).unwrap();
      const patientOpt = allPatients.find((p) => p.id === patientId);
      const therapistOpt = therapistsList.find((t) => t.id === therapistId);
      if (patientOpt && !patientsForTherapist.some((p) => p.id === patientId)) {
        setPatientsForTherapist((prev) => [...prev, patientOpt]);
      }
      if (therapistOpt && !therapistsForPatient.some((t) => t.id === therapistId)) {
        setTherapistsForPatient((prev) => [
          ...prev,
          { id: therapistOpt.id, guid: therapistOpt.guid || "", fullName: therapistOpt.name },
        ]);
      }
      setAssociationError("");
    } catch (err) {
      console.error("Failed to link therapist-patient:", err);
    }
  };

  const selectedTherapistName = useMemo(() => {
    const fromList = therapistsList.find((t) => t.id === therapistId)?.name;
    if (fromList) return fromList;
    return therapistsForPatient.find((t) => t.id === therapistId)?.fullName || "";
  }, [therapistsList, therapistsForPatient, therapistId]);

  // Validation Effect
  useEffect(() => {
    const validate = async () => {
      if (!therapistId || !date || !startTime || !endTime || appointmentType !== 'treatment') return;
      
      setIsValidating(true);
      try {
        const result = await validateChange({
          appointmentId: 0,
          therapistsGuid: therapistGuid || undefined,
          usersGuid: patientGuid || undefined,
          date: date,
          startTime: startTime,
          endTime: endTime,
        }).unwrap();

        setConflicts(result.conflicts || []);
        setTherapistAvailability(result.therapistAvailability || []);
      } catch (error) {
        console.error("Validation error:", error);
      } finally {
        setIsValidating(false);
      }
    };

    const timeout = setTimeout(validate, 500);
    return () => clearTimeout(timeout);
  }, [date, startTime, endTime, therapistId, therapistGuid, patientGuid, appointmentType, validateChange]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (therapistDropdownRef.current && !therapistDropdownRef.current.contains(event.target as Node)) {
        setShowTherapistDropdown(false);
      }
      if (mediaDropdownRef.current && !mediaDropdownRef.current.contains(event.target as Node)) {
        setShowMediaDropdown(false);
      }
      if (patientDropdownRef.current && !patientDropdownRef.current.contains(event.target as Node)) {
        setShowPatientDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isStartBeforeEnd = timeToMinutes(startTime) < timeToMinutes(endTime);

  const durationOptions = useMemo(
    () =>
      appointmentsProgramsData.AppointmentsDurations.map((d) => ({
        value: Number(d.value ?? d.Id),
        label: `${d.Minutes ?? d.label} ${t("MINUTES_SUFFIX")}`,
      })),
    [appointmentsProgramsData.AppointmentsDurations]
  );

  const sequenceOptions = useMemo(
    () =>
      appointmentsProgramsData.AppointmentsSequence.map((s) => ({
        value: Number(s.value ?? s.Id),
        label: String(s.label ?? s.CountInPeriod),
      })),
    [appointmentsProgramsData.AppointmentsSequence]
  );

  /**
   * A device protocol states how many sessions a week the treatment runs, so the frequency is not
   * a question to ask again when planning the series — it is already decided, and letting the user
   * pick a different one would schedule a course the protocol never prescribed.
   *
   * PeriodsId 1 is the weekly period; the matching row is the one whose count equals it.
   */
  /**
   * A device protocol states how often the course runs, using the same frequency catalogue the
   * treatment plan uses — so the plan does not ask again, it just shows what was decided.
   */
  const lockedSequenceId = deviceProtocolTemplate?.appointmentsSequenceId ?? 0;

  useEffect(() => {
    if (lockedSequenceId) setAppointmentsSequenceId(lockedSequenceId);
  }, [lockedSequenceId]);

  const sessionDurationMinutes = useMemo(() => {
    return timeToMinutes(endTime) - timeToMinutes(startTime);
  }, [startTime, endTime]);

  // Plan is valid once the modal was saved (planConfigured), the frequency is set,
  // and the appointment has a positive duration (derived from start/end times).
  const seriesSettingsValid =
    repeatCount <= 1 ||
    (planConfigured && appointmentsSequenceId > 0 && sessionDurationMinutes > 0);

  const handleRepeatCountChange = useCallback(
    (newCount: number) => {
      setRepeatCount((previousCount) => {
        if (previousCount === 1 && newCount > 1 && !hasAutoOpened) {
          setIsPlanPanelOpen(true);
          setHasAutoOpened(true);
        }
        return newCount;
      });
    },
    [hasAutoOpened]
  );

  const handleDurationChange = useCallback((id: number) => {
    setDurationManuallySet(true);
    setAppointmentsDurationsId(id);
  }, []);

  // The cadence is already the number the server stores, so this only has to drop the count from
  // the four cadences that do not take one — the server rejects a stray everyNSessions.
  const buildProtocolsPayload = useCallback(
    (): IAppointmentProgramProtocol[] =>
      selectedProtocols.map((p) => ({
        protocolId: p.protocolId,
        frequencyType: p.frequencyType,
        everyNSessions: protocolFrequencyTakesEveryN(p.frequencyType)
          ? p.everyNSessions
          : null,
      })),
    [selectedProtocols]
  );

  // Auto-select duration matching start/end time difference
  useEffect(() => {
    if (durationManuallySet || durationOptions.length === 0 || sessionDurationMinutes <= 0) {
      return;
    }

    const closest = durationOptions.reduce((best, opt) => {
      const minutes = parseInt(String(opt.label), 10);
      if (isNaN(minutes)) return best;
      const bestMinutes = best ? parseInt(String(best.label), 10) : Infinity;
      return Math.abs(minutes - sessionDurationMinutes) <
        Math.abs(bestMinutes - sessionDurationMinutes)
        ? opt
        : best;
    }, null as (typeof durationOptions)[0] | null);

    if (closest) {
      setAppointmentsDurationsId(Number(closest.value));
    }
  }, [durationOptions, sessionDurationMinutes, durationManuallySet]);

  const handleCreate = async () => {
    if (appointmentType === 'treatment' && (!patientId || (!therapistId && !requiresDevice))) return;
    if (appointmentType === 'other' && !therapistId) return;
    if (rescanDue) {
      setDeviceError(t("TREATMENT_RESCAN_DUE"));
      return;
    }
    if (requiresDevice && !appointmentDevice) {
      setDeviceError(t("DEVICE_SESSION_REQUIRED"));
      return;
    }

    setAssociationError("");
    setDeviceError("");

    try {
      await postAppointment({
        appointmentDate: date,
        startTime: startTime,
        endTime: endTime,
        // A device session belongs to the machine, so it is sent with no therapist even if one
        // was picked before the patient's device protocol loaded.
        therapistsId: requiresDevice ? 0 : therapistId,
        therapistsGuid: requiresDevice ? "" : therapistGuid,
        usersId: appointmentType === 'treatment' ? patientId : null,
        usersGuid: appointmentType === 'treatment' ? patientGuid : null,
        mediaId: appointmentType === 'treatment' ? mediaId : null,
        comments: comments,
        companyId: admin.companyId || 0,
        countOfAppointments: repeatCount,
        appointmentsDurationsId: repeatCount > 1 ? appointmentsDurationsId : null,
        // Duration (minutes) drives the program's duration; harmless for a plain single.
        appointmentsDurationMinutes: sessionDurationMinutes,
        // Frequency only applies to a multi-session series.
        appointmentsSequenceId: repeatCount > 1 ? appointmentsSequenceId : null,
        protocols: appointmentType === 'treatment' ? buildProtocolsPayload() : [],
        // Plan details from TreatmentPlanModal. Sent for single too — the backend only
        // spins up a program when at least one of these (or a protocol) is present.
        treatmentPlanName: planDetails.treatmentPlanName,
        roleId: planDetails.roleId,
        genderId: planDetails.genderId,
        migzarId: planDetails.migzarId,
        treatmentRoomId: planDetails.treatmentRoomId,
        fundingSourcesId: planDetails.fundingSourcesId,
        treatmentTypesId: planDetails.treatmentTypesId,
        device: appointmentDevice ?? undefined,
      }).unwrap();
      onSuccess();
    } catch (error: unknown) {
      const err = error as { data?: { code?: string; message?: string; desc?: string } };
      if (err?.data?.code === "THERAPIST_PATIENT_NOT_LINKED") {
        setAssociationError(err.data?.message || t("APPOINTMENTS_THERAPIST_PATIENT_NOT_LINKED"));
      } else if (err?.data?.message || err?.data?.desc) {
        setDeviceError(err.data?.message || err.data?.desc || t("SOMETHING_WENT_WRONG_TRY_AGAIN_LATER"));
      } else {
        console.error("Failed to create appointment:", error);
      }
    }
  };

  const formatDate = (dateStr: string): string => {
    try {
        const date = new Date(dateStr);
        return date.toISOString().split('T')[0];
    } catch {
        return dateStr;
    }
  };

  const dayNames = [
    t("SUNDAY"), t("MONDAY"), t("TUESDAY"), t("WEDNESDAY"), t("THURSDAY"), t("FRIDAY"), t("SATURDAY")
  ];
  const getDayName = (dayId: number) => dayNames[dayId - 1] || "";

  const mediaTypes = [
    { id: 1, name: t("MEDIA_CHAT") },
    { id: 2, name: t("MEDIA_VIDEO") },
    { id: 3, name: t("MEDIA_PHONE") },
    { id: 4, name: t("MEDIA_CLINIC") },
  ];

  return (
    <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal} dir={isRtl ? "rtl" : "ltr"}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>
            <span>➕</span>
            <span>{t("APPOINTMENTS_NEW_TITLE")}</span>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        <div className={styles.modalBody}>
          {/* Type Toggle */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '20px',
            backgroundColor: '#f3f4f6',
            borderRadius: '8px',
            padding: '4px',
            gap: '4px'
          }}>
            <button
              onClick={() => setAppointmentType('treatment')}
              style={{
                flex: 1,
                padding: '8px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: appointmentType === 'treatment' ? '#fff' : 'transparent',
                boxShadow: appointmentType === 'treatment' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                cursor: 'pointer',
                fontWeight: appointmentType === 'treatment' ? 600 : 400,
                color: appointmentType === 'treatment' ? '#8b5cf6' : '#6b7280',
                transition: 'all 0.2s'
              }}
            >
              {t("TREATMENT") || "טיפול"}
            </button>
            <button
              onClick={() => setAppointmentType('other')}
              style={{
                flex: 1,
                padding: '8px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: appointmentType === 'other' ? '#fff' : 'transparent',
                boxShadow: appointmentType === 'other' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                cursor: 'pointer',
                fontWeight: appointmentType === 'other' ? 600 : 400,
                color: appointmentType === 'other' ? '#8b5cf6' : '#6b7280',
                transition: 'all 0.2s'
              }}
            >
              {t("OTHER") || "אחר"}
            </button>
          </div>

          {appointmentType === 'treatment' && (
            /* Patient Selection */
            <div className={styles.formGroup} ref={patientDropdownRef}>
              <label className={styles.label}>
                <span className={styles.labelIcon}>👤</span>
                {t("APPOINTMENTS_PATIENT")}
              </label>
              <div className={styles.customSelect}>
                <div
                  className={`${styles.customSelectTrigger} ${showPatientDropdown ? styles.customSelectActive : ''}`}
                  onClick={() => setShowPatientDropdown(!showPatientDropdown)}
                >
                  <span className={styles.customSelectValue}>
                    {patientId ? `👤 ${selectedPatientName}` : t("SELECT_PATIENT")}
                  </span>
                  <span className={styles.customSelectArrow}>▼</span>
                </div>
                {showPatientDropdown && (
                  <div className={styles.customSelectDropdown}>
                    <div className={styles.searchContainer}>
                      <input
                        type="text"
                        className={styles.input}
                        style={{ minHeight: '36px', padding: '8px 12px', fontSize: '14px' }}
                        placeholder={t("SEARCH_PLACEHOLDER")}
                        value={patientSearch}
                        onChange={(e) => setPatientSearch(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                      />
                    </div>
                    <div className={styles.dropdownOptions}>
                      {patientId > 0 && (
                        <div
                          className={styles.dropdownOption}
                          onClick={() => {
                            setPatientId(0);
                            setPatientGuid("");
                            setSelectedPatientName("");
                            setShowPatientDropdown(false);
                          }}
                          style={{ borderBottom: "1px solid #e5e7eb" }}
                        >
                          <span className={styles.optionText} style={{ color: "#6b7280", fontStyle: "italic" }}>
                            ✕ {t("CLEAN_THE_SELECTION") || t("CLEAR") || "נקה בחירה"}
                          </span>
                        </div>
                      )}
                      {(isSearchingPatients || (therapistId && isFetchingLinked)) ? (
                        <div className={styles.dropdownOption} style={{ justifyContent: 'center', cursor: 'default' }}>
                          <span className={styles.optionText}>{t("LOADING")}...</span>
                        </div>
                      ) : patientOptions.length > 0 ? (
                        patientOptions.map((p) => (
                          <div
                            key={p.id}
                            className={`${styles.dropdownOption} ${patientId === p.id ? styles.dropdownOptionSelected : ''}`}
                            onClick={() => {
                              setPatientId(p.id);
                              setPatientGuid(p.guid);
                              setSelectedPatientName(p.fullName);
                              setShowPatientDropdown(false);
                            }}
                          >
                            <span className={styles.optionText}>
                              {p.isLinked && <span style={{ marginInlineEnd: 6, color: '#22c55e' }}>✓</span>}
                              {p.fullName}
                            </span>
                            {patientId === p.id && <span className={styles.checkmark}>✓</span>}
                          </div>
                        ))
                      ) : (
                        <div className={styles.dropdownOption} style={{ justifyContent: 'center', cursor: 'default' }}>
                          <span className={styles.optionText}>{t("NO_RESULTS")}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className={styles.formGroup}>
            <label className={styles.label}>
              <span className={styles.labelIcon}>📅</span>
              {t("APPOINTMENTS_DATE")}
            </label>
            <input
              type="date"
              className={styles.input}
              value={formatDate(date)}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <span className={styles.labelIcon}>⏰</span>
                {t("APPOINTMENTS_START_TIME")}
              </label>
              <input
                type="time"
                className={styles.input}
                value={startTime}
                style={(!isStartBeforeEnd) ? { borderColor: '#ef4444', backgroundColor: '#fef2f2' } : {}}
                onChange={(e) => {
                  setStartTime(e.target.value);
                  const [h, m] = e.target.value.split(":").map(Number);
                  if (!isNaN(h)) {
                    const d = new Date();
                    d.setHours(h, m + 50);
                    setEndTime(d.toTimeString().slice(0, 5));
                  }
                }}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>{t("APPOINTMENTS_END_TIME")}</label>
              <input
                type="time"
                className={styles.input}
                value={endTime}
                disabled={Boolean(lockedSessionMinutes)}
                onChange={(e) => setEndTime(e.target.value)}
              />
              {Boolean(lockedSessionMinutes) && (
                <span className={styles.hintText}>
                  {t("DEVICE_DURATION_LOCKED_HINT")} · {lockedSessionMinutes} {t("MINUTES_SHORT")}
                </span>
              )}
            </div>
            {!isStartBeforeEnd && startTime && endTime && (
              <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '-10px', marginBottom: '10px' }}>
                  {t("START_TIME_BEFORE_END_TIME_ERROR") || "שעת הסיום חייבת להיות אחרי שעת ההתחלה"}
              </div>
          )}
          </div>

          {appointmentType === 'treatment' && (
            <>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <span className={styles.labelIcon}>🔢</span>
                {t("NUMBER_OF_APPOINTMENTS") || "מספר פגישות"}
              </label>
              <div className={seriesStyles.countButtons}>
                {[10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map((num) => (
                  <button
                    key={num}
                    onClick={() => handleRepeatCountChange(num)}
                    type="button"
                    disabled={sessionsRemaining !== null && num > sessionsRemaining}
                    className={`${seriesStyles.countButton} ${
                      repeatCount === num ? seriesStyles.countButtonSelected : ""
                    }`}
                  >
                    {num}
                  </button>
                ))}
              </div>
              <div className={seriesStyles.countHint}>
                {/* What is left of the protocol period caps the series, so the limit is stated
                    next to the choice instead of only failing on save. */}
                {sessionsRemaining !== null && sessionsRemaining > 0
                  ? `${t("TREATMENT_SESSIONS_REMAINING")}: ${sessionsRemaining}`
                  : t("SESSION_COUNT_HINT")}
              </div>
            </div>

            <div className={styles.formGroup}>
              <button
                type="button"
                className={seriesStyles.configurePlanBtn}
                onClick={() => setShowPlanModal(true)}
              >
                <span className={seriesStyles.configurePlanIcon}>🗓️</span>
                <span className={seriesStyles.configurePlanText}>
                  {repeatCount > 1
                    ? t("CONFIGURE_TREATMENT_PLAN") || "הגדר תוכנית טיפול"
                    : t("CONFIGURE_PROTOCOLS") || "הגדר פרוטוקולים"}
                </span>
                {(() => {
                  // Multi = full plan (requires frequency). Single = protocols only.
                  if (repeatCount > 1) {
                    if (planConfigured && appointmentsSequenceId > 0) {
                      const parts = [
                        sequenceOptions.find((o) => o.value === appointmentsSequenceId)?.label,
                        `${sessionDurationMinutes} ${t("MINUTES_SUFFIX") || "דקות"}`,
                        selectedProtocols.length > 0
                          ? `${selectedProtocols.length} ${t("PROTOCOLS") || "פרוטוקולים"}`
                          : null,
                      ].filter(Boolean);
                      return (
                        <span
                          className={seriesStyles.configurePlanSummary}
                          style={{ display: "flex", flexDirection: "column", gap: "2px", textAlign: "start" }}
                        >
                          {planDetails.treatmentPlanName && (
                            <strong>{planDetails.treatmentPlanName}</strong>
                          )}
                          <span>{parts.join(" | ")}</span>
                        </span>
                      );
                    }
                    return (
                      <span className={seriesStyles.configurePlanRequired}>
                        {t("REQUIRED") || "חובה"}
                      </span>
                    );
                  }

                  // Single: show protocol count when set, otherwise "optional".
                  return selectedProtocols.length > 0 ? (
                    <span className={seriesStyles.configurePlanSummary}>
                      {`${selectedProtocols.length} ${t("PROTOCOLS") || "פרוטוקולים"}`}
                    </span>
                  ) : (
                    <span className={seriesStyles.configurePlanSummary}>
                      {t("OPTIONAL") || "אופציונלי"}
                    </span>
                  );
                })()}
              </button>
            </div>

            {showPlanModal && (
              <TreatmentPlanModal
                companyId={admin.companyId}
                repeatCount={repeatCount}
                appointmentsDurationsId={appointmentsDurationsId}
                appointmentsSequenceId={appointmentsSequenceId}
                calculatedDurationMinutes={sessionDurationMinutes}
                selectedProtocols={selectedProtocols}
                durationOptions={durationOptions}
                sequenceOptions={sequenceOptions}
                // Decided by the device protocol, so the field is shown but not open for a
                // second opinion.
                frequencyLocked={Boolean(lockedSequenceId)}
                protocols={protocolsData}
                roleOptions={appointmentsProgramsData.Roles}
                genderOptions={appointmentsProgramsData.Genders}
                migzarOptions={appointmentsProgramsData.Migzars}
                treatmentRoomOptions={appointmentsProgramsData.TreatmentRoomsList}
                fundingSourcesOptions={appointmentsProgramsData.FundingSources}
                treatmentTypesOptions={appointmentsProgramsData.TreatmentTypes}
                mediaLabel={mediaTypes.find((m) => m.id === mediaId)?.name || ""}
                initialDetails={planDetails}
                onClose={() => setShowPlanModal(false)}
                onSave={(v) => {
                  setAppointmentsDurationsId(v.appointmentsDurationsId);
                  setAppointmentsSequenceId(v.appointmentsSequenceId);
                  setSelectedProtocols(v.protocols);
                  setDurationManuallySet(true);
                  setPlanConfigured(true);
                  // Collect section-2 plan details for the POST request
                  setPlanDetails({
                    treatmentPlanName: v.treatmentPlanName,
                    roleId: v.roleId,
                    genderId: v.genderId,
                    migzarId: v.migzarId,
                    treatmentRoomId: v.treatmentRoomId,
                    fundingSourcesId: v.fundingSourcesId,
                    treatmentTypesId: v.treatmentTypesId,
                  });
                }}
              />
            )}
            </>
          )}

          {/* Therapist Field - Now visible for both types, except a device session: that one is
              booked against the machine, and every technician in the company sees it. */}
          {!requiresDevice && (
          <div className={styles.formGroup} ref={therapistDropdownRef}>
            <label className={styles.label}>
              <span className={styles.labelIcon}>👨‍⚕️</span>
              {t("APPOINTMENTS_THERAPIST")}
            </label>
            <div className={styles.customSelect}>
              <div
                className={`${styles.customSelectTrigger} ${showTherapistDropdown ? styles.customSelectActive : ''}`}
                onClick={() => setShowTherapistDropdown(!showTherapistDropdown)}
              >
                <span className={styles.customSelectValue}>
                  {therapistId ? `👨‍⚕️ ${selectedTherapistName}` : t("SELECT_THERAPIST")}
                </span>
                <span className={styles.customSelectArrow}>▼</span>
              </div>
              {showTherapistDropdown && (
                <div className={styles.customSelectDropdown}>
                  <div className={styles.searchContainer}>
                    <input
                      type="text"
                      className={styles.input}
                      style={{ minHeight: '36px', padding: '8px 12px', fontSize: '14px' }}
                      placeholder={t("SEARCH_PLACEHOLDER")}
                      value={therapistSearch}
                      onChange={(e) => setTherapistSearch(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  </div>
                  <div className={styles.dropdownOptions}>
                    {appointmentType === "treatment" && therapistId > 0 && (
                      <div
                        className={styles.dropdownOption}
                        onClick={() => {
                          setTherapistId(0);
                          setTherapistGuid("");
                          setAppointmentDevice(null);
                          setStaffDevices([]);
                          setShowTherapistDropdown(false);
                        }}
                        style={{ borderBottom: "1px solid #e5e7eb" }}
                      >
                        <span className={styles.optionText} style={{ color: "#6b7280", fontStyle: "italic" }}>
                          ✕ {t("CLEAN_THE_SELECTION") || t("CLEAR") || "נקה בחירה"}
                        </span>
                      </div>
                    )}
                    {patientId && isFetchingLinked ? (
                      <div className={styles.dropdownOption} style={{ justifyContent: 'center', cursor: 'default' }}>
                        <span className={styles.optionText}>{t("LOADING")}...</span>
                      </div>
                    ) : (
                    therapistOptions.map((th) => (
                      <div
                        key={th.id}
                        className={`${styles.dropdownOption} ${therapistId === th.id ? styles.dropdownOptionSelected : ''}`}
                        onClick={() => handleTherapistSelect(th.id, th.guid || "")}
                      >
                        <span className={styles.optionText}>
                          {th.isLinked && <span style={{ marginInlineEnd: 6, color: '#22c55e' }}>✓</span>}
                          👨‍⚕️ {th.fullName}
                          {th.hasDevices && <span style={{ marginInlineStart: 6 }}>🔌</span>}
                        </span>
                        {therapistId === th.id && <span className={styles.checkmark}>✓</span>}
                      </div>
                    ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          )}

          {/* Shown as soon as the patient's protocol calls for a device — the machine is picked
              from the company's list, so nothing here waits on which technician was chosen. */}
          {requiresDevice && (
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <span className={styles.labelIcon}>🔌</span>
                {t("DEVICE_SESSION")}
              </label>
              {/* The series is over: say so where the device is configured, since that is the
                  thing that can no longer happen. */}
              {rescanDue ? (
                <div className={styles.currentValueWrapper}>
                  <div className={styles.currentValue}>{t("TREATMENT_RESCAN_DUE")}</div>
                </div>
              ) : appointmentDevice ? (
                <div className={styles.currentValueWrapper}>
                  <div className={styles.currentValue}>
                    {getDeviceSummaryLabel(appointmentDevice)}
                  </div>
                  <button
                    type="button"
                    className={styles.changeButton}
                    onClick={() => {
                      if (staffDevices.length > 0) {
                        setShowDeviceModal(true);
                      } else {
                        openDeviceConfiguration();
                      }
                    }}
                  >
                    {t("CHANGE")}
                  </button>
                </div>
              ) : (
                <>
                  {/* No device bound yet: show what the protocol will apply, without a device
                      name or room since neither has been chosen. */}
                  {deviceProtocolTemplate && (
                    <div className={styles.currentValueWrapper}>
                      <div className={styles.currentValue}>
                        {getDeviceConfigSummary(deviceProtocolTemplate)}
                        {deviceProtocolTemplate.durationMinutes
                          ? ` · ${deviceProtocolTemplate.durationMinutes} ${t("MINUTES_SHORT")}`
                          : ""}
                      </div>
                      <span className={styles.hintText}>{t("DEVICE_FROM_PROTOCOL")}</span>
                    </div>
                  )}
                  <button
                    type="button"
                    className={styles.changeButton}
                    onClick={() => openDeviceConfiguration()}
                    disabled={isFetchingDevices}
                  >
                    {isFetchingDevices ? t("LOADING") : t("CONFIGURE_DEVICE")}
                  </button>
                </>
              )}
            </div>
          )}

          {deviceError && (
            <div
              className={styles.warningsSection}
              style={{ borderColor: "#ef4444", backgroundColor: "#fef2f2" }}
            >
              <div className={`${styles.warningItem} ${styles.warningHigh}`}>
                <span className={styles.warningIcon}>⚠️</span>
                <span>{deviceError}</span>
              </div>
            </div>
          )}

          {appointmentType === 'treatment' && (
            /* Media Field */
            <div className={styles.formGroup} ref={mediaDropdownRef}>
              <label className={styles.label}>
                <span className={styles.labelIcon}>📍</span>
                {t("APPOINTMENTS_MEDIA")}
              </label>
              <div className={styles.customSelect}>
                <div
                  className={`${styles.customSelectTrigger} ${showMediaDropdown ? styles.customSelectActive : ''}`}
                  onClick={() => setShowMediaDropdown(!showMediaDropdown)}
                >
                  <span className={styles.customSelectValue}>
                    {mediaTypes.find(m => m.id === mediaId)?.name || t("SELECT_MEDIA")}
                  </span>
                  <span className={styles.customSelectArrow}>▼</span>
                </div>
                {showMediaDropdown && (
                  <div className={styles.customSelectDropdown}>
                    <div className={styles.dropdownOptions}>
                      {mediaTypes.map((media) => (
                        <div
                          key={media.id}
                          className={`${styles.dropdownOption} ${mediaId === media.id ? styles.dropdownOptionSelected : ''}`}
                          onClick={() => {
                            setMediaId(media.id);
                            setShowMediaDropdown(false);
                          }}
                        >
                          <span className={styles.optionText}>{media.name}</span>
                          {mediaId === media.id && <span className={styles.checkmark}>✓</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className={styles.formGroup}>
            <label className={styles.label}>
              <span className={styles.labelIcon}>📝</span>
              {appointmentType === 'treatment' ? t("APPOINTMENTS_NOTES") : (t("MESSAGE_CONTENT") || "תוכן ההודעה")}
            </label>
            <textarea
              className={styles.textarea}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={3}
              placeholder={t("APPOINTMENTS_NOTES_PLACEHOLDER")}
            />
          </div>

          {/* Association prompt - therapist/patient not linked, with link button */}
          {appointmentType === 'treatment' && patientId && therapistId && !isPatientTherapistLinked && (
            <div
              className={styles.warningsSection}
              style={{ borderColor: "#f59e0b", backgroundColor: "#fffbeb" }}
            >
              <div className={styles.warningItem} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                <span className={styles.warningIcon}>ℹ️</span>
                <span>{t("APPOINTMENTS_THERAPIST_PATIENT_NOT_LINKED_PROMPT")}</span>
                <button
                  type="button"
                  onClick={handleLinkTherapistPatient}
                  disabled={isLinking}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 6,
                    border: 'none',
                    background: '#8b5cf6',
                    color: 'white',
                    fontWeight: 600,
                    cursor: isLinking ? 'not-allowed' : 'pointer',
                    opacity: isLinking ? 0.7 : 1,
                  }}
                >
                  {isLinking ? t("LOADING") + "..." : t("APPOINTMENTS_LINK")}
                </button>
              </div>
            </div>
          )}

          {/* Association Error - from backend when create fails */}
          {associationError && (
            <div
              className={styles.warningsSection}
              style={{ borderColor: "#ef4444", backgroundColor: "#fef2f2" }}
            >
              <div className={styles.warningsTitle} style={{ color: "#dc2626" }}>
                <span>❌</span>
                {t("APPOINTMENTS_WARNINGS")}
              </div>
              <div className={`${styles.warningItem} ${styles.warningHigh}`}>
                <span className={styles.warningIcon}>⚠️</span>
                <span>{associationError}</span>
              </div>
            </div>
          )}

          {/* Warnings Section */}
          {conflicts.length > 0 && (
            <div className={styles.warningsSection}>
              <div className={styles.warningsTitle}>
                <span>⚠️</span>
                {t("APPOINTMENTS_WARNINGS")}
              </div>
              <div className={styles.warningsList}>
                {conflicts.map((conflict, index) => (
                  <div
                    key={index}
                    className={`${styles.warningItem} ${conflict.severity === 'warning' ? styles.warningHigh : styles.warningLow}`}
                  >
                    <span className={styles.warningIcon}>
                      {conflict.severity === 'warning' ? '⚠️' : 'ℹ️'}
                    </span>
                    <span>{conflict.message}</span>
                    {conflict.type === 'therapist_not_available' && therapistAvailability.length > 0 && (
                      <div className={styles.availabilityTooltip}>
                        <span className={styles.availabilityLabel}>📅 {t("THERAPIST_AVAILABILITY")}:</span>
                        <div className={styles.availabilityList}>
                          {therapistAvailability.map((av, idx) => (
                            <div key={idx} className={styles.availabilityItem}>
                              {getDayName(av.dayId)}: {av.startTime} - {av.endTime}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.cancelBtn} onClick={onClose}>{t("APPOINTMENTS_CLOSE")}</button>
          <button
            className={styles.saveBtn}
            onClick={handleCreate}
            disabled={
              isCreating ||
              !isStartBeforeEnd ||
              (appointmentType === 'treatment' &&
                (!patientId || (!therapistId && !requiresDevice) || !isPatientTherapistLinked || !seriesSettingsValid || !protocolsValid ||
                  rescanDue ||
                  exceedsRemainingSessions ||
                  (requiresDevice && !appointmentDevice))) ||
              (appointmentType === 'other' && !therapistId) ||
              isValidating
            }
          >
            {isCreating ? t("SAVING") : t("APPOINTMENTS_SAVE")}
          </button>
        </div>
      </div>

      {showDeviceModal && staffDevices.length > 0 && (
        <DeviceConfigurationModal
          devices={staffDevices}
          initialDevice={appointmentDevice}
          protocolTemplate={deviceProtocolTemplate}
          protocolRequired={appointmentType === "treatment" && Boolean(patientId)}
          onConfirm={(device) => {
            setAppointmentDevice(device);
            setShowDeviceModal(false);
            setDeviceError("");
            // The session length configured for the device is the appointment's length, so a
            // change here follows through instead of leaving the two out of step.
            applyProtocolDuration(device.durationMinutes);
          }}
          onCancel={() => {
            setShowDeviceModal(false);
            if (!appointmentDevice) {
              setTherapistId(0);
              setTherapistGuid("");
            }
          }}
        />
      )}
    </div>
  );
};

export default CreateAppointmentModal;
